import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, catchError, from, switchMap, throwError } from 'rxjs';

import { HankoAuthService } from '@app/security/feature/hanko/data-access/hanko-auth.service';

import { NotificationService } from '@shared/features/notification/data-access/notification.service';

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
}

// A token specifically for skipping error notification handling
export const SKIP_ERROR_NOTIFICATION_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

const isProblemDetails = (error: HttpErrorResponse): boolean =>
  error.error !== null && error.error !== undefined && typeof error.error === 'object';

const createGenericProblemDetails = (error: HttpErrorResponse): ProblemDetails => ({
  type: 'about:blank',
  title: error.statusText || 'Nieznany błąd',
  status: error.status,
  detail: error.message || 'Wystąpił błąd. Spróbuj ponownie później.',
  instance: error.url || window.location.href,
});

export const errorNotificationInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const router = inject(Router);
  const hankoAuthService = inject(HankoAuthService);

  if (req.context.get(SKIP_ERROR_NOTIFICATION_INTERCEPTOR)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpStatusCode.Unauthorized) {
        return from(hankoAuthService.signOut()).pipe(
          switchMap(() => {
            router.navigate(['/errors/401']);
            return EMPTY;
          })
        );
      }

      const problemDetails: ProblemDetails = isProblemDetails(error)
        ? (error.error as ProblemDetails)
        : createGenericProblemDetails(error);

      notificationService.showError(problemDetails.detail);

      if (error.status === HttpStatusCode.Forbidden) {
        return throwError(() => error);
      }

      // For 4xx errors, show notification but don't propagate
      if (error.status >= 400 && error.status < 500) {
        return EMPTY;
      }

      // For other errors (5xx, network errors etc), propagate to Sentry
      return throwError(() => error);
    })
  );
};
